import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { JSX, useState } from 'react';
import { useHistory } from 'react-router';
import { isNonEmptyArray } from '@apollo/client/utilities';
import {
	useActivateRegularAccountMutation,
	useDeactivateRegularAccountMutation,
} from '@copilot/data/graphql/_generated';
import notificationManager from '@copilot/common/utils/notificationManager';
import { isEmptyString } from '@copilot/common/utils/common';
import { useFeatureToggle } from '@copilot/common/hooks/feature';
import { Features } from '@copilot/data/responses/interface';
import { useImpersonationWithNewTab } from '../adminHub/useImpersonation';
import AccountTable from './accountTable';
import AccountDashboardHeader from './accountDashboardHeader';
import { useAccounts, useCurrentAccount } from './data/hook';
import { Account, AccountDashboardFilters } from './types';
import { Modal } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import {
	DEFAULT_ACCOUNT_DASHBOARD_FILTERS,
	EMPTY_DATA_MESSAGE,
	NO_RESULT_MESSAGE,
} from '@copilot/common/pages/accountDashboard/const';
import styles from './accountDashboardPage.module.less';
import isError from 'lodash/isError';
import ErrorIcon from '@copilot/common/components/icon/errorIcon';

/**
 * Represents an account dashboard.
 * @constructor
 * @return {JSX.Element} The account dashboard component.
 */
export default function AccountDashboardPage(): JSX.Element {
	const [modal, contextHolder] = Modal.useModal();
	const isAccountDashboardEnabled = useFeatureToggle(Features.AccountDashboardFeature);
	const isAccountDeleteEnabled = useFeatureToggle(Features.AccountDeleteFeature);
	const { data: accountData } = useCurrentAccount();
	const [{ data: accountsData, loading: loadingAccounts }] = useAccounts();
	const [deactivate] = useDeactivateRegularAccountMutation();
	const [reactivate] = useActivateRegularAccountMutation();
	const [filters, setFilters] = useState<AccountDashboardFilters>(
		DEFAULT_ACCOUNT_DASHBOARD_FILTERS
	);
	const onImpersonation = useImpersonationWithNewTab();
	const history = useHistory();

	const navigateToAccountDetailsPage = (accountId: string) => {
		history.push(`dashboard/${accountId}`);
	};

	const handleAccountCreated = (createdAccountId: string) => {
		navigateToAccountDetailsPage(createdAccountId);
	};

	/*
		Filter function for accounts in table
	 */
	function meetsFilterCriteria(account: Account) {
		const { searchUppercase, accountManagerId, accountStatus, linkedInDisconnected } = filters;
		const meetsSearch =
			isNil(searchUppercase) ||
			isEmptyString(searchUppercase) ||
			account.name.toUpperCase().includes(searchUppercase);
		const meetsAccountManager =
			isNil(accountManagerId) || account.accountManager?.id === accountManagerId;
		const meetsAccountStatus = isNil(accountStatus) || account.status === accountStatus;
		const meetsLinkedInDisconnected = !linkedInDisconnected || account.linkedInConnection > 0;
		return (
			meetsSearch && meetsAccountManager && meetsAccountStatus && meetsLinkedInDisconnected
		);
	}

	function openDeactivateAccountModal(accountId: string, name: string) {
		modal.confirm({
			okText: 'Deactivate Account',
			title: `Deactivate ${name}`,
			cancelText: 'Cancel',
			onCancel: () => {},
			icon: <InfoCircleFilled />,
			width: 600,
			content: (
				<div>
					<p>Deactivating this account will:</p>
					<ul>
						<li>Prevent associated users from logging in.</li>
						<li>Block your access to this customer’s account.</li>
						<li>
							Display a message to users upon login attempt, informing them that
							access is restricted and advising them to contact support for
							reactivation.
						</li>
						<li>Suspend all email notifications linked to this account.</li>
					</ul>
					<h4>Are you sure you want to deactivate this account?</h4>
					<p>
						Note: You can reactivate this account at any time. All team members'
						accounts, including those previously deactivated, will be reactivated.
					</p>
				</div>
			),
			onOk: async () => {
				try {
					await deactivate({ variables: { accountId }, refetchQueries: ['accounts'] });
					notificationManager.showSuccessNotification({
						message: 'Account successfully deactivated',
					});
				} catch (reason) {
					notificationManager.showErrorNotification({
						message: 'Update Failed',
						description: isError(reason) ? reason.message : undefined,
					});
				}
			},
		});
	}

	function openReactivateAccountModal(accountId: string, name: string) {
		modal.confirm({
			okText: 'Reactivate Account',
			title: `Reactivate ${name}`,
			cancelText: 'Cancel',
			onCancel: () => {},
			icon: <InfoCircleFilled />,
			width: 600,
			content: (
				<div>
					<p>Reactivating this account will:</p>
					<ul>
						<li>Enable users to log in again.</li>
						<li>Reactivate only the primary contact.</li>
						<li>Resume all associated email notifications.</li>
					</ul>
					<h4>Are you sure you want to reactivate this account?</h4>
				</div>
			),
			onOk: async () => {
				try {
					await reactivate({ variables: { accountId }, refetchQueries: ['accounts'] });

					notificationManager.showSuccessNotification({
						message: 'Account successfully reactivated',
					});
				} catch (reason) {
					notificationManager.showErrorNotification({
						message: 'Update Failed',
						description: isError(reason) ? reason.message : undefined,
					});
				}
			},
		});
	}

	function openDeleteAccountModal(accountId: string, name: string) {
		modal.confirm({
			okText: 'Delete Account',
			okButtonProps: { danger: true },
			title: `Delete ${name}`,
			cancelText: 'Cancel',
			icon: <ErrorIcon />,
			width: 450,
			content: (
				<div>
					<p>Deleting this account will:</p>
					<ul>
						<li>Delete all users and their data.</li>
						<li>Lose your access to this customer’s account.</li>
					</ul>
					<h4>
						This change will be permanent. Are you sure you want to delete this account?
					</h4>
				</div>
			),
			onOk: () => {
				//TODO integrate delete account call
			},
		});
	}

	if (!isAccountDashboardEnabled)
		return <>Sorry, you do not have access to this part of the application.</>;

	const accounts = accountsData?.accounts ?? [];
	const filteredData = accounts.filter(meetsFilterCriteria);
	const accountManagers = sortBy(
		uniqBy(
			compact([
				...(accountData?.admin_account?.users.map((u) => ({
					id: u.id,
					firstName: u.firstName,
					lastName: u.lastName,
				})) ?? []), //managers in this account
				...(accountsData?.accounts.map((a) => a.accountManager) ?? []), //managers already in this list
			]),
			'id'
		),
		'firstName'
	);
	return (
		<>
			<AccountDashboardHeader
				accounts={accountsData?.accounts ?? []}
				accountManagers={accountManagers}
				filters={filters}
				loading={loadingAccounts}
				updateFilterCallback={setFilters}
				userCounts={{
					activeUserCount: accountsData?.activeUserCount ?? 0,
					totalUserCount: accountsData?.totalUserCount ?? 0,
				}}
				onAccountCreated={handleAccountCreated}
			/>
			<div className={styles.accountDashboardPage}>
				<AccountTable
					loading={loadingAccounts}
					data={filteredData}
					showAccountManager={true}
					onImpersonation={onImpersonation}
					accountManagers={accountManagers}
					onAccountDeactivate={openDeactivateAccountModal}
					onAccountActivate={openReactivateAccountModal}
					onAccountSelected={navigateToAccountDetailsPage}
					onAccountDelete={isAccountDeleteEnabled ? openDeleteAccountModal : undefined}
					emptyText={
						isNonEmptyArray(accountsData?.accounts)
							? NO_RESULT_MESSAGE
							: EMPTY_DATA_MESSAGE
					}
				/>
			</div>
			{contextHolder}
		</>
	);
}
